import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';

interface ReasoningEntry {
  reasoning: string;
  datetime?: string;
  geography?: string;
  language?: string;
}

interface ReasoningPanelProps {
  entries: ReasoningEntry[];
  status: 'idle' | 'connecting' | 'streaming' | 'error';
  error?: string | null;
  interactionId?: string;
}

export const ReasoningPanel: React.FC<ReasoningPanelProps> = ({ entries, status, error, interactionId }) => {
  if (status === 'idle') {
    return null;
  }

  const isConnecting = status === 'connecting';
  const hasEntries = entries && entries.length > 0;
  const placeholderEntry: ReasoningEntry = {
    reasoning: 'Gathering relevant knowledge…',
    datetime: undefined,
    geography: undefined,
    language: undefined
  };
  const displayEntries: ReasoningEntry[] = hasEntries ? entries : [placeholderEntry];

  return (
    <div className="mb-4 w-full animate-fadeIn">
      {/* Subtle header */}
      <div className="mb-2 flex items-center gap-2 px-1">
        <Sparkles className="h-3.5 w-3.5 text-amber-600/70" />
        <span className="text-xs font-medium text-gray-500">
          {status === 'error' ? 'Thinking interrupted' : 'Thinking'}
        </span>
        {status === 'streaming' && !error && (
          <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
        )}
      </div>

      {/* Thinking content - inline style */}
      <div className="space-y-1">
        {error ? (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        ) : (
          <>
            {displayEntries.map((entry, index) => {
              const reasoningText = entry.reasoning || 'Gathering relevant knowledge…';
              const key = entry.datetime ? `${entry.datetime}-${index}` : `placeholder-${index}`;

              return (
                <div
                  key={key}
                  className="group flex items-start gap-2 px-1 py-1.5 text-sm text-gray-600 opacity-0"
                  style={{
                    animation: `fadeIn 0.3s ease ${index * 50}ms forwards`
                  }}
                >
                  <span className="mt-0.5 text-xs text-gray-400">•</span>
                  <span className="leading-relaxed">{reasoningText}</span>
                </div>
              );
            })}
            
            {status === 'streaming' && (
              <div className="flex items-center gap-2 px-1 py-1 text-xs text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="italic">Still thinking…</span>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// Demo
export default function Demo() {
  const [status, setStatus] = React.useState<'idle' | 'connecting' | 'streaming' | 'error'>('streaming');
  const [entries, setEntries] = React.useState<ReasoningEntry[]>([
    { reasoning: 'Analyzing the user\'s request for a Claude-style thinking display' },
    { reasoning: 'Considering the visual design patterns Claude uses for extended thinking' },
    { reasoning: 'Planning a minimal, inline approach without prominent card styling' }
  ]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setEntries(prev => [...prev, 
        { reasoning: 'Refining the component to match Claude\'s subtle presentation style' }
      ]);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 rounded-xl bg-white p-6 shadow-sm">
          <h1 className="mb-2 text-2xl font-semibold text-gray-800">Claude-Style Thinking</h1>
          <p className="text-sm text-gray-600">
            Subtle, inline display that integrates naturally into the conversation flow
          </p>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm">
          <ReasoningPanel 
            entries={entries}
            status={status}
            interactionId="demo-123"
          />
          
          <div className="mt-6 border-t pt-4">
            <p className="text-gray-700">
              I've redesigned the thinking display to match Claude's inline style. The key changes include:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-gray-600">
              <li>• Removed the prominent card/border styling</li>
              <li>• Used subtle bullet points for each thought</li>
              <li>• Minimal header with just an icon and label</li>
              <li>• Inline integration that flows naturally</li>
              <li>• Simplified animations and reduced visual weight</li>
            </ul>
          </div>

          <div className="mt-6 flex gap-2">
            <button
              onClick={() => setStatus('streaming')}
              className="rounded-lg bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600"
            >
              Streaming
            </button>
            <button
              onClick={() => setStatus('connecting')}
              className="rounded-lg bg-gray-500 px-4 py-2 text-sm text-white hover:bg-gray-600"
            >
              Connecting
            </button>
            <button
              onClick={() => setStatus('error')}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600"
            >
              Error
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}